import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Description from '@components/typography/Description';

import { CventContact } from '@customTypes/shared/CventContact';
import { Column } from '@customTypes/table';

import { fetchPendingCventContacts } from '@actions/cventContacts';
import {
    getCventPendingContacts,
    getCventContactsCurrentPage,
    getCventContactsIsFetching,
    getCventContactsPageSize,
    getCventContactsTotalCount,
    getCventContactsTotalPages,
} from '@selectors/cventContacts';

import LinkButton from '@components/button/LinkButton';
import TableWithFetch from '@components/tableWithFetch/TableWithFetch';

import ButtonRow from '@components/button/ButtonRow';
import dayjs from 'dayjs';

const ConflictManagementCventContactsTable: React.FC = () => {
    const dispatch = useDispatch();
    const isFetching = useSelector(getCventContactsIsFetching);
    const contacts = useSelector(getCventPendingContacts);
    const currentPage = useSelector(getCventContactsCurrentPage);
    const pageSize = useSelector(getCventContactsPageSize);
    const totalPages = useSelector(getCventContactsTotalPages);
    const totalCount = useSelector(getCventContactsTotalCount);

    const fetchData = useCallback(
        (page: number, pageSize: number, searchTerm: string) => {
            dispatch(fetchPendingCventContacts(page, pageSize, searchTerm));
        },
        [dispatch],
    );

    return (
        <>
            <Description>Here is a list of all the pending Cvent contacts.</Description>
            <TableWithFetch
                columns={columns}
                rows={Object.values(contacts)}
                isLoading={isFetching}
                fetchData={fetchData}
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={totalPages}
                totalCount={totalCount}
                searchType="cmCventContacts"
            />
        </>
    );
};

const columns: Column<CventContact>[] = [
    {
        key: 1,
        heading: 'Name',
        getValue: row =>
            `${row.firstName ? row.firstName : ''} ${row.lastName ? row.lastName : ''}`,
        searchable: true,
    },
    {
        key: 2,
        heading: 'Email address',
        getValue: row => `${row.userEmail}`,
        searchable: true,
    },
    {
        key: 3,
        heading: 'Phone',
        getValue: row => (row.phone ? row.phone : ''),
        searchable: true,
    },
    {
        key: 4,
        heading: 'Twitter',
        getValue: row => (row.twitter ? row.twitter : ''),
        searchable: true,
    },
    {
        key: 5,
        heading: 'Last Updated',
        getValue: row => (row.lastUpdated ? dayjs(row.lastUpdated).format('DD/MM/YYYY') : ''),
        searchable: true,
    },
    {
        key: 6,
        heading: 'Last Synced',
        getValue: row => (row.lastSynced ? dayjs(row.lastSynced).format('DD/MM/YYYY') : ''),
        searchable: true,
    },
    {
        key: 7,
        heading: '',
        getValue: row => (
            <ButtonRow alignment="right">
                <LinkButton href={`/conflict-management/cvent-contacts/${row.id}`}>
                    View details
                </LinkButton>
            </ButtonRow>
        ),
        searchable: false,
    },
];

export default ConflictManagementCventContactsTable;
